import React from 'react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Label,
  LabelList,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';

const COLORS = {
  RED: '#FF6666',
  ORANGE: '#FFB266',
  GREEN: '#00CC00'
};

const RANGES = {
  LOW: 25,
  MID: 75,
  HIGH: 100
};

/**
 * ClusterHBarChart
 * @param data
 * @param width
 * @param height
 * @param onBarClicked
 * @param barIndex
 * @param xLabel
 * @param yLabel
 * @returns {*}
 * @constructor
 */
export const ClusterHBarChart = ({
  data,
  width,
  height,
  onBarClicked,
  barIndex,
  xLabel,
  yLabel
}) => {
  if (!data) {
    return <div />;
  }

  return (
    <div style={{ marginTop: '20px', marginLeft: '-10px' }}>
      <BarChart
        cursor="pointer"
        layout="vertical"
        width={width}
        height={height}
        data={data}>
        <CartesianGrid strokeDasharray="5 5" />
        <XAxis
          type="number"
          label={{ value: xLabel || '', position: 'center' }}
          tick={false}
          orientation="top"
        />
        <YAxis type="category" dataKey="clusterName" />
        <Bar dataKey="count" fill="#3786A6" minPointSize={10}>
          <LabelList dataKey="count" position="insideRight" fill={'#fff'} />
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={index == barIndex ? '#b32cd1' : '#3786A6'}
              onClick={(e) => {
                onBarClicked(index);
              }}
            />
          ))}
        </Bar>
        <Tooltip cursor={null} />
      </BarChart>
    </div>
  );
};

/**
 * ClusterBarChart
 * @param data
 * @param dataKey
 * @param xDataKey
 * @param width
 * @param height
 * @param xLabel
 * @param yLabel
 * @param fill
 * @param dataKeyNormalized
 * @returns {*}
 * @constructor
 */
export const ClusterBarChart = ({
  data,
  dataKey,
  xDataKey,
  width,
  height,
  xLabel,
  yLabel,
  fill,
  dataKeyNormalized
}) => {
  if (!data || !dataKey || !xDataKey) {
    return <div />;
  }

  const { RED, ORANGE, GREEN } = COLORS;
  const { LOW, MID, HIGH } = RANGES;

  return (
    <div
      style={{
        marginTop: '20px',
        marginLeft: '-20px',
        height: '600px',
        paddingLeft: '45px'
      }}>
      <ComposedChart
        width={width || 0}
        height={height || 0}
        data={data}
        margin={{ top: 10, right: 20, left: 20, bottom: 40 }}>
        <ReferenceLine
          stokeWidth="0.5"
          y={HIGH}
          label={{
            position: 'insideTopRight',
            value: 'High',
            fill: RED,
            fontSize: 12
          }}
          stroke={RED}
        />

        <ReferenceLine
          stokeWidth="0.5"
          y={MID}
          label={{
            position: 'insideTopRight',
            value: 'Medium',
            fill: ORANGE,
            fontSize: 12
          }}
          stroke={ORANGE}
        />

        <ReferenceLine
          y={LOW}
          label={{
            position: 'insideTopRight',
            value: 'Low',
            fill: GREEN,
            fontSize: 12
          }}
          stroke={GREEN}
        />

        <XAxis
          dataKey={xDataKey}
          label={{ value: xLabel || '', position: 'center' }}
          interval={0}
        />
        <YAxis tick={false} type="number" domain={[0, 1]} unit="%">
          <Label
            offset={-4}
            angle={-90}
            value={yLabel}
            position="insideLeft"
            style={{
              textAnchor: 'middle'
            }}
          />
          />
        </YAxis>
        <Tooltip content={<CustomerTooltip dataKey={dataKey} />} />
        <Bar
          dataKey={dataKeyNormalized}
          fill={GREEN}
          barSize={10}
          minPointSize={10}>
          {/* <LabelList dataKey={dataKey} position="top" /> */}
          {data.map((entry, index) => {
            let color;
            if (
              entry[dataKeyNormalized] > 0 &&
              entry[dataKeyNormalized] <= LOW
            ) {
              color = GREEN;
            } else if (
              entry[dataKeyNormalized] > LOW &&
              entry[dataKeyNormalized] <= MID
            ) {
              color = ORANGE;
            } else if (
              entry[dataKeyNormalized] > MID &&
              entry[dataKeyNormalized] <= HIGH
            ) {
              color = RED;
            } else {
              color = GREEN;
            }
            return <Cell fill={color} />;
          })}
        </Bar>
      </ComposedChart>
    </div>
  );
};
/**
 * CustomerTooltip
 * @param props
 * @returns {null|*}
 * @constructor
 */
const CustomerTooltip = (props) => {
  const { active, payload, dataKey } = props;
  if (active) {
    if (
      Array.isArray(payload) &&
      payload.length > 0 &&
      payload[0] &&
      payload[0].payload
    ) {
      const clusterInformation = payload[0].payload;
      let formattedAttributes = [];

      if (clusterInformation) {
        const clusterAtrributes = clusterInformation.attributes;

        if (Array.isArray(clusterAtrributes)) {
          clusterAtrributes.forEach((attribute) => {
            let currentClusterAttribute = null;
            try {
              currentClusterAttribute = JSON.parse(attribute);
            } catch (err) {
              currentClusterAttribute = null;
            }

            if (currentClusterAttribute) {
              let clusterComparison = '';
              switch (currentClusterAttribute.comparison) {
                case 'H':
                  clusterComparison = 'High';
                  break;
                case 'M':
                  clusterComparison = 'Medium';
                  break;
                case 'L':
                  clusterComparison = 'Low';
                  break;
                default:
                  clusterComparison = currentClusterAttribute.comparison;
                  break;
              }
              formattedAttributes.push(
                <div className="col-sm-4">
                  <div className="row">
                    <p style={{ fontSize: '12px' }} className="col-sm-9">
                      {currentClusterAttribute.name}
                    </p>
                    <p style={{ fontSize: '12px' }} className="col-md-3">
                      {' '}
                      {clusterComparison}{' '}
                    </p>
                  </div>
                </div>
              );
            }
          });
        }
      }

      return (
        <div
          className="row"
          style={{
            background: '#fff',
            border: '1px solid',
            borderRadius: '5px',
            width: '900px',
            margin: '0',
            padding: 0
          }}>
          <div className="col-sm-12">
            <div
              style={{
                background: '#1c3145',
                color: '#fff',
                paddingTop: '10px'
              }}
              className="row">
              <div className="col-sm-12">
                <h4
                  style={{
                    textAlign: 'center',
                    color: '#fff'
                  }}>
                  {clusterInformation.clusterName}
                </h4>
                <h5 style={{ textAlign: 'center', color: '#fff' }}>
                  Cluster Size: {clusterInformation.count}
                </h5>
                <h5
                  style={{
                    textAlign: 'center',
                    color: '#fff'
                  }}>
                  {dataKey}:{' '}
                  {clusterInformation[dataKey] &&
                    clusterInformation[dataKey].toFixed(2)}
                </h5>
              </div>
            </div>

            <div className="row">
              <div className="col-sm-12">
                <hr />
              </div>

              {formattedAttributes}
            </div>
          </div>
        </div>
      );
    }
  }

  return null;
};
